"use client";

import React, { useState, useEffect, useMemo } from 'react';
import { FilterSidebar } from '@/components/explorer/FilterSidebar';
import { ResultsList } from '@/components/explorer/ResultsList';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { HeroSection } from '@/components/shared/HeroSection';
import { fetchJsonData, validateCCMTData } from '@/lib/data-handlers';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ExplorerPage() {
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    rounds: [] as string[],
    institutes: [] as string[],
    programs: [] as string[],
    categories: [] as string[],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedYear, setSelectedYear] = useState("2026_round1");

  // Load data with error handling
  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchJsonData(`/ccmt_${selectedYear}.json`);
        if (result.error) {
          setError(result.error);
          setData([]);
        } else if (result.data) {
          setData(result.data);
          setError(null);
        } else {
          setError('No data received');
          setData([]);
        }
      } catch (err: any) {
        setError(err.message || 'Unexpected error loading data');
        setData([]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [selectedYear]);

  // Compute unique values for filters (interdependent)
  const availableValues = useMemo(() => {
    if (data.length === 0) return { rounds: [], institutes: [], programs: [], categories: [] };

    const getFilteredValues = (excludeKey: string) => {
      return data.filter(item => {
        const matchRound = excludeKey === 'rounds' || filters.rounds.length === 0 || filters.rounds.includes(item.round);
        const matchInstitute = excludeKey === 'institutes' || filters.institutes.length === 0 || filters.institutes.includes(item.institute);
        const matchProgram = excludeKey === 'programs' || filters.programs.length === 0 || filters.programs.includes(item.program);
        const matchCategory = excludeKey === 'categories' || filters.categories.length === 0 || filters.categories.includes(item.category);
        return matchRound && matchInstitute && matchProgram && matchCategory;
      });
    };

    return {
      rounds: Array.from(new Set(data.map(item => item.round))).sort((a, b) => {
        if (a === "National Spot Round") return 1;
        if (b === "National Spot Round") return -1;
        return a.localeCompare(b);
      }),
      institutes: Array.from(new Set(getFilteredValues('institutes').map(item => item.institute))).sort(),
      programs: Array.from(new Set(getFilteredValues('programs').map(item => item.program))).sort(),
      categories: Array.from(new Set(getFilteredValues('categories').map(item => item.category))).sort(),
    };
  }, [data, filters]);

  // Filtering logic
  const filteredData = useMemo(() => {
    if (data.length === 0) return [];

    return data.filter(item => {
      const matchRound = filters.rounds.length === 0 || filters.rounds.includes(item.round);
      const matchInstitute = filters.institutes.length === 0 || filters.institutes.includes(item.institute);
      const matchProgram = filters.programs.length === 0 || filters.programs.includes(item.program);
      const matchCategory = filters.categories.length === 0 || filters.categories.includes(item.category);

      return matchRound && matchInstitute && matchProgram && matchCategory;
    });
  }, [data, filters]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, selectedYear]);

  const removeFilter = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key as keyof typeof prev].filter(v => v !== value)
    }));
  };

  const clearFilters = () => {
    setFilters({
      rounds: [],
      institutes: [],
      programs: [],
      categories: [],
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-destructive/10 border border-destructive/20 rounded-3xl p-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="size-16 rounded-full bg-destructive/10 border border-destructive/20 flex items-center justify-center">
                <AlertCircle className="size-8 text-destructive" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground mb-2">Unable to Load Data</h2>
              <p className="text-sm text-on-surface-variant leading-relaxed">{error}</p>
            </div>
            <div className="flex gap-3 justify-center pt-4">
              <Button
                onClick={() => window.location.reload()}
                className="bg-primary text-primary-foreground"
              >
                Retry
              </Button>
              <Button
                onClick={() => window.history.back()}
                variant="outline"
              >
                Go Back
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <main className="container mx-auto px-4 py-8 sm:px-6 lg:py-16 max-w-full overflow-hidden">
        <HeroSection 
          title={<>GATE <span className="text-primary">CCMT Cutoff</span> Explorer <br className="hidden md:block" /> (2021-2026)</>}
          description={<>Analyze comprehensive historical GATE cutoff trends and opening/closing ranks across <span className="text-on-surface font-bold">NITs, IIITs, and GFTIs</span>. Filter by program, category, and round.</>}
        />

        <div className="mt-0 flex flex-col lg:flex-row gap-8 items-start w-full overflow-hidden">
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            availableValues={availableValues}
          />

          <ResultsList
            results={filteredData}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalCount={filteredData.length}
            filters={filters}
            onRemoveFilter={removeFilter}
            onClearFilters={clearFilters}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        </div>

        {/* Static Editorial Content — Crawlable by Google */}
        <section className="mt-20 max-w-4xl mx-auto space-y-10 pb-10">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-display font-black text-foreground mb-3">How to Use the CCMT Cutoff Explorer</h2>
            <p className="text-on-surface-variant text-sm md:text-base max-w-2xl mx-auto">A complete guide to reading CCMT opening and closing ranks, understanding year-wise trends, and using filters effectively.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">Understanding Opening & Closing Ranks</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                In CCMT seat allotment, the <strong className="text-foreground">Opening Rank</strong> (Max Score) is the highest GATE score that received a seat in a given program-round combination. The <strong className="text-foreground">Closing Rank</strong> (Min Score) is the lowest GATE score that was allotted a seat — this is the actual cutoff. If your GATE score is above the closing rank for a program, you are likely eligible for that seat in that counseling round.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">How Cutoffs Change Across Rounds</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                CCMT typically conducts 3 regular rounds followed by Special Rounds. Cutoffs generally <strong className="text-foreground">decrease</strong> from Round 1 to later rounds as candidates with higher scores withdraw to join preferred institutions. Candidates who receive a seat in Round 1 and choose "Float" can potentially upgrade to a better college in subsequent rounds. Analyzing round-wise trends in this explorer helps you identify which programs consistently have declining cutoffs.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">Year-on-Year Cutoff Variation</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                GATE exam difficulty fluctuates every year, which directly impacts CCMT cutoffs. In years when a GATE paper is harder than average, raw scores are lower, and so are CCMT cutoffs. Our explorer covers data from <strong className="text-foreground">2021 through 2026</strong>, giving you a 6-year window to identify whether a program&apos;s cutoff is trending upward, downward, or remaining stable. Use the year selector to switch between datasets.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-surface-container border border-outline-variant/20 space-y-3">
              <h3 className="font-bold text-lg text-foreground">Filtering by Category</h3>
              <p className="text-on-surface-variant text-sm leading-relaxed">
                CCMT seat allotment follows the Government of India&apos;s reservation policy. Categories include <strong className="text-foreground">OPEN (General), OBC-NCL, SC, ST, EWS</strong>, and their respective PwD (Persons with Disabilities) sub-categories. Cutoffs differ significantly between categories — an OBC-NCL cutoff for a program can be 50-100 points lower than the OPEN cutoff for the same program at the same institute. Always filter by your own category for accurate comparison.
              </p>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-primary/5 border border-primary/20">
            <h3 className="font-bold text-lg text-foreground mb-3">Pro Strategy: How to Build an Optimal Preference List</h3>
            <p className="text-on-surface-variant text-sm leading-relaxed mb-3">
              The most effective approach to CCMT choice filling is to use this explorer to identify three tiers of colleges: <strong className="text-foreground">Aspirational</strong> (your score is below the last year&apos;s closing rank — but worth including as cutoffs can drop), <strong className="text-foreground">Target</strong> (your score is near or at the historical closing range), and <strong className="text-foreground">Safe</strong> (your score comfortably exceeds the historical closing rank). A well-balanced list of 40-50 choices spanning all three tiers maximizes your probability of securing the best possible seat.
            </p>
            <p className="text-on-surface-variant text-sm leading-relaxed">
              Remember: CCMT allows up to 100 choices. Don&apos;t waste this opportunity. Use the program filter here to check the eligibility of your GATE paper code for each specialization, and cross-reference with NIRF rankings to prioritize by institute quality.
            </p>
          </div>
        </section>

      </main>
    </div>
  );
}
